import { use, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { BookText, Lock } from "lucide-react";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";
import { useTerms } from "@/hooks/use-terms";
import { cameBackFromTrip } from "../../terms/ReturnTrip";
import { TerminologyContext } from "../../terms/Terminology";
import { TermsBrowser } from "../../terms/TermsPanel";
import { ReviewCard } from "./ReviewCard";

/**
 * The terms the run will use, as the Terminology panel lists them, with the
 * same way to edit one where it was defined, and what generating does to them.
 *
 * What the card shows when the attorney comes to the review is kept in the
 * shell, and the start sends it, so the run's lock can count the terms edited
 * from here; coming back from editing one keeps it.
 */
export function TermsCard({ taskId }: { taskId: string }) {
  const terminology = use(TerminologyContext);
  const terms = useTerms(taskId, true);
  const view = terms.data;
  const fresh = !cameBackFromTrip(useLocation().state);
  const notedRef = useRef(false);
  useEffect(() => {
    // Noted once the read the visit asked for has landed, never from a stale cache.
    if (view === undefined || terms.isFetching) return;
    if (terminology === null || notedRef.current) return;
    notedRef.current = true;
    terminology.review.note(view, fresh);
  });
  return (
    <ReviewCard
      icon={<BookText />}
      title="Terminology"
      meta="Locked when you generate"
    >
      <p className="max-w-[70ch] text-[13px] leading-normal text-pretty text-muted-foreground">
        These are the terms Praxis will use to build the draft. Use{" "}
        <b className="text-foreground">Terms only</b> for a quick scan, or
        search to check whether Praxis uses a particular word. To change a term,
        edit it where it was defined and you will be brought back here.
      </p>
      <TermsBrowser
        view={view}
        seen={null}
        onGo={(kind, term, edit) => {
          terminology?.go(kind, term, edit);
        }}
        pending={terminology?.trip.opened ?? null}
        layout="card"
      />
      <Panel
        tone="brand"
        shape="row"
        pad="tight"
        className="mt-3.5 flex items-start gap-2.5 text-detail leading-normal text-pretty"
      >
        <IconTile size="xs">
          <Lock />
        </IconTile>
        <p>
          <b>No action needed.</b> When you generate, Praxis locks these terms
          and uses each one exactly as written, with no synonyms. If Praxis
          thinks a locked term is wrong or inconsistent, it keeps the term and
          marks the passage in red for you to review.
        </p>
      </Panel>
    </ReviewCard>
  );
}
